import { MinimumSlideInformation, SlideDetails } from "./slide";

/** Variant names of a stored favorite image set (the favorites blob contract). */
export type FavoriteImageVariantName = 'high' | 'med' | 'low' | 'thumb_n' | 'thumb_h';

/**
 * Pixel dimensions of each stored favorite image variant. Mirrors the service's
 * `PCP.Function.ImageConverter` `ImageDefinitions` — the pinned client↔service contract
 * (`canvas-slide-favorites` D2/D9). Fixed sizes apply to 16:9 slides; panorama exports ONE
 * size for the whole ladder — the full extent at native content resolution, uploaded once and
 * fanned out to the three stored names server-side (content-pipeline parity). Thumbs always use
 * the exact sizes below.
 */
export const FAVORITE_IMAGE_VARIANTS: Readonly<Record<FavoriteImageVariantName, { width: number; height: number }>> = {
    high: { width: 3840, height: 2160 },
    med: { width: 1920, height: 1080 },
    low: { width: 1280, height: 720 },
    thumb_n: { width: 240, height: 135 },
    thumb_h: { width: 256, height: 144 },
};

/**
 * The client-generated favorite image set for a canvas slide (`panorama-single-part-upload`).
 * Two mutually-exclusive shapes, distinguished by whether the display ladder has distinct steps:
 *
 * - **uniform ladder** — one image serves `high`/`med`/`low` (panorama: one native resolution).
 *   It is uploaded ONCE, as `high`; the service stores those bytes under `med`/`low` too.
 * - **derived ladder** — `med`/`low` are real downscales of the master, each uploaded.
 *
 * `med?: never` / `low?: never` is what makes the arms exclusive rather than one being a structural
 * subset of the other (same idiom as {@link SaveFavoriteMetadata}'s `images?: never`). It means a
 * derived export CANNOT compile without its downscales, and `'med' in images` narrows the union.
 *
 * The thumbs are in both arms and always uploaded: they are distinct save-time viewport crops, never
 * ladder steps. Nothing on the wire declares the shape — the parts present are the whole signal, and
 * the service fills any absent `med`/`low` from `high`.
 */
export type FavoriteImageSet =
    | { high: Blob; med?: never; low?: never; thumb_n: Blob; thumb_h: Blob }
    | { high: Blob; med: Blob; low: Blob; thumb_n: Blob; thumb_h: Blob };

/**
 * What a "save as favorite" click produced:
 * - `video` — a video slide; carries the Vimeo id, sent in the JSON metadata request.
 * - `image` — the client-generated image set for fabric-canvas slides, uploaded as multipart.
 * A payload-less save (`null`) is a reference favorite that points at the original slide.
 */
export type FavoriteExportPayload =
    | { kind: 'video'; vimeoVideoId: number }
    | { kind: 'image'; images: FavoriteImageSet };

export interface Favorite extends MinimumSlideInformation {
    /** Source slide's module — identifies the slide content. */
    moduleId: null | number;
    /** Source slide's version — identifies the slide content. */
    versionId: null | number;
    slideId: number;
    /** Module the favorite is filed in (visibility), independent of the source slide's module. */
    assignedModuleId: number;
    /** Lection within the assigned module, or null when unassigned. */
    assignedLectionId: null | number;
    lectionName?: null | string;  //no lection name while creating
    data: null | string;
}

/** Metadata shared by every save-favorite request. */
export interface SaveFavoriteBase {
    /** Source slide's module — identifies the slide content. */
    moduleId: number;
    /** Source slide's version — identifies the slide content. */
    versionId: number;
    /** Module the favorite is filed in (visibility). Required, must be > 0. */
    assignedModuleId: number;
    /** Lection within the assigned module, or null when unassigned. */
    assignedLectionId: null | number;
    slideId: number;
    type: SlideTypeCategoryLetter;
}

/** JSON metadata contract: video favorites carry `vimeoVideoId`; reference favorites send null. */
export interface SaveFavoriteMetadata extends SaveFavoriteBase {
    vimeoVideoId: number | null;
    images?: never;
}

/** Canvas contract (`canvas-slide-favorites`): the client-generated image set, uploaded as multipart. */
export interface SaveFavoriteImages extends SaveFavoriteBase {
    images: FavoriteImageSet;
    vimeoVideoId?: never;
}

/**
 * A POST request to save a new Customer Favorite — exactly one of the two contracts applies
 * (discriminate via `'images' in favorite`).
 */
export type SaveFavorite = SaveFavoriteMetadata | SaveFavoriteImages;

export type FavoriteDetails = Favorite & SlideDetails;

/**
 * similar to "SlideTypeCategory" type but with single letter.
 */
export type SlideTypeCategoryLetter = 'P' | 'V' | 'I' | 'R' | 'M' | 'V';    // 'canvas' | 'video' | 'interactive' | 'rubber' | 'max-question' | 'voting'

export const isFavorite = (object: any): object is Favorite => 'slideId' in object && 'assignedLectionId' in object && 'data' in object;

export const isFavoriteDetails = (object: any): object is FavoriteDetails => 'slideId' in object && 'assignedLectionId' in object && 'data' in object;
